import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin } from 'antd';
import toyotaLogo from '../../assets/TOYOTA LOGO - Horizontal.svg';
import carServiceBookingSvg from '../../assets/car-service-booking.svg';
import noDataSvg from '../../assets/no-data.svg';
import useLineLiff from '../../hooks/LineLiff';
import { fetchBookingAvailable } from './CarServiceBookingAction';

import './common.scss';
import './CarServiceBookingRecord.scss';

export default function CarServiceBookingRecord() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { liff } = useLineLiff(process.env.REACT_APP_LIFF_BOOKING);

  const carNo = searchParams.get('carNo');
  const sKey = searchParams.get('sKey');

  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);

  // 確保頁面加載時滾動到頂部
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // 取得預約紀錄
  useEffect(() => {
    if (!carNo) return;
    const loadRecords = async () => {
      try {
        await liff.ready;
        const idToken = liff.getIDToken();
        if (idToken && carNo) {
          const response = await fetchBookingAvailable(idToken, carNo);
          if (response.success && response.data) {
            const { booking_list = [] } = response.data;

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const formattedRecords = booking_list.map((item, index) => ({
              id: item.s_key || `r-${index}`,
              type: item.work_type === '23' ? 'maintenance' : 'paint',
              center: item.brnh_name || '',
              centerFull: `${item.dlr_name || ''} ${item.brnh_name || ''
                }`.trim(),
              dateTime:
                item.book_date && item.book_time
                  ? `${item.book_date} ${item.book_time}`
                  : '',
              plate: carNo,
              workNo: item.work_no || '',
              bpEvaNo: item.bp_eva_no || '',
              serviceItem: item.work_type === '23' ? '保養維修' : '板噴估價',
              dlrCode: item.dlr_code || '',
              brnhCode: item.brnh_code || '',
              sKey: item.s_key || '',
              dlrName: item.dlr_name || '',
              brnhName: item.brnh_name || '',
            }))
              .filter((record) => {
                if (!record.dateTime) return false;
                const normalized = record.dateTime.replace(/\//g, '-').replace(' ', 'T');
                const recordDate = new Date(normalized);
                recordDate.setHours(0, 0, 0, 0);
                return recordDate >= today;
              })
              .sort((a, b) => {
                const dateA = new Date(a.dateTime.replace(/\//g, '-').replace(' ', 'T'));
                const dateB = new Date(b.dateTime.replace(/\//g, '-').replace(' ', 'T'));
                return dateA - dateB;
              });

            setRecords(formattedRecords);
          }
        }
      } catch (error) {
        console.error('Failed to fetch booking records:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRecords();
  }, [carNo]);

  const handleTabClick = (path) => {
    const params = new URLSearchParams();
    if (carNo) params.set('carNo', carNo);
    if (sKey) params.set('sKey', sKey);
    const queryString = params.toString();
    navigate(queryString ? `${path}?${queryString}` : path);
  };

  const handleCardClick = (record) => {
    navigate('/hotai/toyota/car-service-booking/record-detail', { state: record });
  };

  return (
    <div className="center-viewport">
      <div className="maintenance-app">
        {/* Toyota Logo */}
        <div className="toyota-header">
          <img className="toyota-logo" src={toyotaLogo} alt="TOYOTA LOGO" />
        </div>

        <div className="main-content">
          {/* Header */}
          <div className="content-header">
            <div className="header-row">
              <div className="header-text">
                <div className="vehicle-number">{carNo || ''}</div>
                <h1 className="page-title">愛車預約管理</h1>
              </div>
              <div className="header-icons">
                <img src={carServiceBookingSvg} alt="預約管理icon" />
              </div>
            </div>

            {/* 3 個功能 Tab */}
            <div className="csb-tabs">
              <button
                type="button"
                className="csb-tab-btn"
                onClick={() =>
                  handleTabClick('/hotai/toyota/car-service-booking')
                }
              >
                保養維修
              </button>
              <button
                type="button"
                className="csb-tab-btn"
                onClick={() =>
                  handleTabClick('/hotai/toyota/car-service-booking/paint')
                }
              >
                板噴估價
              </button>
              <button type="button" className="csb-tab-btn csb-tab-btn--active">
                預約紀錄
              </button>
            </div>
          </div>

          {/* 警告橫幅 */}
          <div className="csb-recall-warning">
            如您對預約紀錄或其預約內容有任何疑慮，請電洽服務據點。
          </div>

          {/* 紀錄列表 */}
          {loading ? (
            <div className="bkr-loading">
              <Spin size="large" />
            </div>
          ) : records.length === 0 ? (
            <div className="bkr-empty">
              <div className="no-data-block">
                <img src={noDataSvg} alt="no data" className="no-data-img" />
                <div className="text-muted-base font-medium">目前沒有預約紀錄</div>
              </div>
            </div>
          ) : (
            <div className="bkr-list">
              {records.map((record) => (
                <div
                  key={record.id}
                  className="bkr-card"
                  onClick={() => handleCardClick(record)}
                >
                  <div className="bkr-card-header">
                    <span className="bkr-card-center">{record.center}</span>
                    <span
                      className={`bkr-card-type bkr-card-type--${record.type}`}
                    >
                      {record.type === 'maintenance' ? '保養維修' : '板噴估價'}
                    </span>
                  </div>
                  <div className="bkr-card-divider" />
                  <div className="bkr-card-time-row">
                    <span className="bkr-card-time-label">預約時段</span>
                    <span className="bkr-card-time-value">
                      {record.dateTime}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
